import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Filter, 
  MoreVertical, 
  CheckCircle2, 
  Clock, 
  AlertCircle, 
  User, 
  Calendar, 
  Trash2, 
  Edit2, 
  Loader2,
  X,
  CheckSquare,
  ListTodo,
  ChevronRight,
  UserPlus,
  MessageSquare,
  Hourglass,
  Send,
  Check,
  Ban
} from 'lucide-react';
import { collection, 
  addDoc, 
  onSnapshot, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  updateDoc,
  doc,
  deleteDoc,
  getDocs } from '../firebase';
import { db, auth, handleFirestoreError, OperationType } from '../firebase';
import { notify } from '../services/notify';
import { cn } from '../lib/utils';
import ConfirmModal from './ConfirmModal';
import { createLocalNotification, createLocalNotifications } from '../services/notificationService';

interface TaskReply {
  id: string;
  text: string;
  userId: string;
  userName: string;
  createdAt: any;
}

interface TaskThread {
  id: string;
  title: string;
  userId: string;
  userName: string;
  createdAt: any;
  replies: TaskReply[];
}

interface ExtensionRequest {
  id: string;
  days: number;
  reason: string;
  status: 'Pending' | 'Approved' | 'Rejected';
  requestedBy: string;
  requestedByName: string;
  requestedAt: any;
}

interface TaskStep {
  id: string;
  title: string;
  description?: string;
  requirements?: string;
  assignedToIds: string[];
  assignedToNames: string[];
  status: 'Pending' | 'In Progress' | 'Completed';
}

interface TaskMOPS {
  id: string;
  title: string;
  description: string;
  assignedToIds: string[];
  assignedToNames: string[];
  priority: 'Low' | 'Medium' | 'High';
  status: 'Pending' | 'In Progress' | 'Completed';
  dueDate: string;
  orgId: string;
  createdAt: any;
  updatedAt: any;
  createdBy: string;
  createdByName?: string;
  threads?: TaskThread[];
  extensionRequests?: ExtensionRequest[];
  isWorkflow?: boolean;
  steps?: TaskStep[];
  currentStepIndex?: number;
}

interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

const normalizeLocalTask = (task: any, orgId: string): TaskMOPS => {
  const metadata = task.metadata || {};
  const source = { ...metadata, ...task };
  const status = String(source.status || 'Pending').toLowerCase();
  const priority = String(source.priority || 'Medium').toLowerCase();
  const dueDate = source.dueDate ?? source.due_date ?? '';

  return {
    id: String(source.id ?? source._id ?? ''),
    title: source.title || '',
    description: source.description || '',
    assignedToIds: source.assignedToIds ?? source.assigned_to_ids ?? [],
    assignedToNames: source.assignedToNames ?? source.assigned_to_names ?? [],
    priority: priority === 'high' ? 'High' : priority === 'low' ? 'Low' : 'Medium',
    status: status === 'completed' ? 'Completed' : status === 'in progress' || status === 'in_progress' ? 'In Progress' : 'Pending',
    dueDate: typeof dueDate === 'string' ? dueDate.split('T')[0] : '',
    orgId: source.orgId ?? source.org_id ?? orgId,
    createdAt: source.createdAt ?? source.created_at,
    updatedAt: source.updatedAt ?? source.updated_at,
    createdBy: source.createdBy ?? source.created_by ?? '',
    createdByName: source.createdByName ?? source.created_by_name,
    threads: source.threads || [],
    extensionRequests: source.extensionRequests ?? source.extension_requests ?? [],
    isWorkflow: source.isWorkflow || false,
    steps: source.steps || [],
    currentStepIndex: source.currentStepIndex || 0
  };
};

export default function TasksMOPSView({ orgId, language = 'en' }: { orgId: string, language?: string }) {
  const [tasks, setTasks] = useState<TaskMOPS[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TaskMOPS | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [isSaving, setIsSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [viewTaskId, setViewTaskId] = useState<string | null>(null);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [newThreadTitle, setNewThreadTitle] = useState('');
  const [newReply, setNewReply] = useState('');
  const [showExtensionModal, setShowExtensionModal] = useState<string | null>(null);
  const [extensionDays, setExtensionDays] = useState(1);
  const [extensionReason, setExtensionReason] = useState('');

  const [newTask, setNewTask] = useState<Omit<TaskMOPS, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'orgId'>>({
    title: '',
    description: '',
    assignedToIds: [],
    assignedToNames: [],
    priority: 'Medium',
    status: 'Pending',
    dueDate: new Date().toISOString().split('T')[0],
    isWorkflow: false,
    steps: [],
    currentStepIndex: 0
  });

  useEffect(() => {
    const handleQuickAction = (e: any) => {
      if (e.detail?.action === 'new-task') {
        setIsEditing(false);
        setNewTask({
          title: '',
          description: '',
          assignedToIds: [],
          assignedToNames: [],
          priority: 'Medium',
          status: 'Pending',
          dueDate: new Date().toISOString().split('T')[0],
          isWorkflow: false,
          steps: [],
          currentStepIndex: 0
        });
        setIsModalOpen(true);
      }
    };
    window.addEventListener('QUICK_ACTION', handleQuickAction as EventListener);
    return () => window.removeEventListener('QUICK_ACTION', handleQuickAction as EventListener);
  }, []);

  useEffect(() => {
    if (!orgId) return;

    // Fetch employees for assignment
    const fetchEmployees = async () => {
      try {
        const localToken = localStorage.getItem('app_auth_token');
        if (localToken) {
          const headers = { Authorization: `Bearer ${localToken}` };
          const [empResp, usersResp] = await Promise.all([
            fetch(`/api/organizations/${orgId}/employees`, { headers }).then(r => r.ok ? r.json() : []),
            fetch(`/api/organizations/${orgId}/users`, { headers }).then(r => r.ok ? r.json() : []),
          ]);
          const emps = (Array.isArray(empResp) ? empResp : []).map((e: any) => ({
            id: e.id, firstName: e.firstName || e.first_name || '', lastName: e.lastName || e.last_name || '', email: e.email || '',
          })) as Employee[];
          const orgUsersData = (Array.isArray(usersResp) ? usersResp : []).map((u: any) => {
            const names = (u.fullName || u.displayName || '').split(' ');
            return { id: u.userId || u.user_id || u.id, firstName: names[0] || 'User', lastName: names.slice(1).join(' ') || '', email: u.email || '' };
          }) as Employee[];
          const combined = [...emps, ...orgUsersData];
          setEmployees(combined.filter((v, i, a) => a.findIndex(t => t.email === v.email) === i));
          return;
        }

        // Fetch from employees collection
        const empQuery = query(collection(db, 'employees'), where('orgId', '==', orgId));
        const empSnap = await getDocs(empQuery);
        const empData = empSnap.docs.map(doc => ({
          id: doc.id,
          firstName: doc.data().firstName,
          lastName: doc.data().lastName,
          email: doc.data().email
        })) as Employee[];

        // Fetch from org_users collection
        const orgUsersQuery = query(collection(db, 'org_users'));
        const orgUsersSnap = await getDocs(orgUsersQuery);
        const orgUsersData = orgUsersSnap.docs
          .map(doc => ({ id: doc.id, ...doc.data() } as any))
          .filter(user => user.memberships?.some((m: any) => m.orgId === orgId))
          .map(user => {
            const names = (user.fullName || '').split(' ');
            return {
              id: user.id,
              firstName: names[0] || 'User',
              lastName: names.slice(1).join(' ') || '',
              email: user.email
            };
          }) as Employee[];

        // Combine and remove duplicates by email
        const combined = [...empData, ...orgUsersData];
        const unique = combined.filter((v, i, a) => a.findIndex(t => t.email === v.email) === i);
        
        setEmployees(unique);
      } catch (error) {
        console.error("Error fetching employees:", error);
        // Don't block the whole view if employee fetch fails
      }
    };

    fetchEmployees();

    const appAuthToken = localStorage.getItem('app_auth_token');
    if (appAuthToken) {
      const controller = new AbortController();

      const loadLocalTasks = async () => {
        try {
          const response = await fetch(`/api/organizations/${orgId}/tasks`, {
            headers: { Authorization: `Bearer ${appAuthToken}` },
            signal: controller.signal
          });
          if (!response.ok) throw new Error(`Failed to load tasks: ${response.status}`);

          const payload = await response.json();
          const rawTasks = Array.isArray(payload) ? payload : payload.tasks ?? payload.data ?? [];
          if (!controller.signal.aborted) {
            setTasks((Array.isArray(rawTasks) ? rawTasks : []).map(task => normalizeLocalTask(task, orgId)));
          }
        } catch (error) {
          if (!controller.signal.aborted) {
            console.error('Error fetching local tasks:', error);
            setTasks([]);
          }
        } finally {
          if (!controller.signal.aborted) setIsLoading(false);
        }
      };

      loadLocalTasks();
      return () => controller.abort();
    }

    const q = query(
      collection(db, 'tasks_mops'),
      where('orgId', '==', orgId),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const tasksData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as TaskMOPS[];
      setTasks(tasksData);
      setIsLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'tasks_mops');
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [orgId]);

  const patchLocalTask = async (taskId: string, updates: Record<string, unknown>) => {
    const appAuthToken = localStorage.getItem('app_auth_token');
    if (!appAuthToken) return false;

    const response = await fetch(`/api/organizations/${orgId}/tasks/${taskId}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${appAuthToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updates)
    });
    if (!response.ok) throw new Error(`Failed to update task: ${response.status}`);

    const updatedTask = normalizeLocalTask(await response.json(), orgId);
    setTasks(currentTasks => currentTasks.map(task => task.id === taskId ? updatedTask : task));
    return true;
  };

  const handleSaveTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgId) return;

    let finalTaskData = { ...newTask };
    
    if (finalTaskData.isWorkflow) {
      if (!finalTaskData.steps || finalTaskData.steps.length === 0) {
        notify.error("Please add at least one step for the workflow");
        return;
      }
      finalTaskData.currentStepIndex = 0;
      finalTaskData.assignedToIds = finalTaskData.steps[0].assignedToIds || [];
      finalTaskData.assignedToNames = finalTaskData.steps[0].assignedToNames || [];
    }

    if (!finalTaskData.assignedToIds || finalTaskData.assignedToIds.length === 0) {
      notify.error(finalTaskData.isWorkflow ? "First step must have assignees" : "Please assign at least one employee");
      return;
    }

    setIsSaving(true);
    try {
      const taskData = {
        ...finalTaskData,
        orgId,
        updatedAt: serverTimestamp()
      };

      if (isEditing && selectedTask) {
        const updatedViaApi = await patchLocalTask(selectedTask.id, finalTaskData);
        if (!updatedViaApi) {
          await updateDoc(doc(db, 'tasks_mops', selectedTask.id), taskData);
        }
        notify.confirm("Task updated successfully");
      } else {
        const appAuthToken = localStorage.getItem('app_auth_token');
        if (appAuthToken) {
          const response = await fetch(`/api/organizations/${orgId}/tasks`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${appAuthToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              ...finalTaskData,
              createdByName: auth.currentUser?.displayName || 'Unknown User',
              threads: [],
              extensionRequests: []
            })
          });
          if (!response.ok) throw new Error(`Failed to create task: ${response.status}`);

          const createdTask = normalizeLocalTask(await response.json(), orgId);
          setTasks(currentTasks => [createdTask, ...currentTasks]);
          await createLocalNotifications(orgId, finalTaskData.assignedToIds, {
            title: 'New Task Assigned',
            message: `A new task "${createdTask.title}" has been assigned to you.`,
            type: 'task_assigned',
            relatedId: createdTask.id,
          });
          notify.confirm("Task created and assigned successfully");
        } else {
          const docRef = await addDoc(collection(db, 'tasks_mops'), {
            ...taskData,
            createdBy: auth.currentUser?.uid,
            createdByName: auth.currentUser?.displayName || 'Unknown User',
            createdAt: serverTimestamp(),
            threads: [],
            extensionRequests: []
          });
          
          // Create notifications for assigned users
          const notificationPromises = newTask.assignedToIds.map(userId => 
            addDoc(collection(db, 'notifications'), {
              userId,
              orgId,
              title: 'New Task Assigned',
              message: `A new task "${newTask.title}" has been assigned to you.`,
              type: 'task_assigned',
              relatedId: docRef.id,
              read: false,
              createdAt: serverTimestamp()
            })
          );
          await Promise.all(notificationPromises);
          
          notify.confirm("Task created and assigned successfully");
        }
      }
      setIsModalOpen(false);
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving task:", error);
      notify.error("Failed to save task");
      handleFirestoreError(error, isEditing ? OperationType.UPDATE : OperationType.CREATE, 'tasks_mops');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteTask = async (id: string) => {
    try {
      const appAuthToken = localStorage.getItem('app_auth_token');
      if (appAuthToken) {
        const response = await fetch(`/api/organizations/${orgId}/tasks/${id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${appAuthToken}` }
        });
        if (!response.ok) throw new Error(`Failed to delete task: ${response.status}`);
        setTasks(currentTasks => currentTasks.filter(task => task.id !== id));
      } else {
        await deleteDoc(doc(db, 'tasks_mops', id));
      }
      notify.confirm("Task deleted successfully");
      setDeleteId(null);
    } catch (error) {
      console.error("Error deleting task:", error);
      notify.error("Failed to delete task");
      handleFirestoreError(error, OperationType.DELETE, 'tasks_mops');
    }
  };

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         (task.assignedToNames || []).some(name => name.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'All' || task.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Completed': return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'In Progress': return <Clock className="w-4 h-4 text-blue-500" />;
      default: return <AlertCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High': return 'text-red-600 bg-red-50 border-red-200';
      case 'Medium': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'Low': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const handleCreateThread = async (taskId: string) => {
    if (!newThreadTitle.trim()) return;
    try {
      const task = tasks.find(t => t.id === taskId);
      if (!task) return;

      const thread: TaskThread = {
        id: Math.random().toString(36).substr(2, 9),
        title: newThreadTitle,
        userId: auth.currentUser?.uid || 'unknown',
        userName: auth.currentUser?.displayName || 'Unknown User',
        createdAt: new Date().toISOString(),
        replies: []
      };

      const updatedThreadsList = [...(task.threads || []), thread];
      const threadViaApi = await patchLocalTask(taskId, { threads: updatedThreadsList });
      if (!threadViaApi) {
        await updateDoc(doc(db, 'tasks_mops', taskId), {
          threads: updatedThreadsList,
          updatedAt: serverTimestamp()
        });
      }

      setNewThreadTitle('');
      
      // Notify other assignees and creator
      const recipients = new Set([...task.assignedToIds, task.createdBy]);
      recipients.delete(auth.currentUser?.uid || '');

      if (localStorage.getItem('app_auth_token')) {
        await createLocalNotifications(orgId, recipients, {
          title: 'New Discussion Thread on Task',
          message: `${auth.currentUser?.displayName || 'Someone'} started a new thread: "${newThreadTitle}"`,
          type: 'task_comment',
          relatedId: taskId,
        });
      } else {
        const notificationPromises = Array.from(recipients).map(userId =>
          addDoc(collection(db, 'notifications'), {
            userId, orgId, title: 'New Discussion Thread on Task',
            message: `${auth.currentUser?.displayName || 'Someone'} started a new thread: "${newThreadTitle}"`,
            type: 'task_comment', relatedId: taskId, read: false, createdAt: serverTimestamp()
          })
        );
        await Promise.all(notificationPromises);
      }
      notify.confirm("Thread started");
    } catch (error) {
      console.error("Error creating thread:", error);
      notify.error("Failed to start thread");
    }
  };

  const handleReplyToThread = async (taskId: string, threadId: string) => {
    if (!newReply.trim()) return;
    try {
      const task = tasks.find(t => t.id === taskId);
      if (!task) return;

      const reply: TaskReply = {
        id: Math.random().toString(36).substr(2, 9),
        text: newReply,
        userId: auth.currentUser?.uid || 'unknown',
        userName: auth.currentUser?.displayName || 'Unknown User',
        createdAt: new Date().toISOString()
      };

      const updatedThreads = (task.threads || []).map(thread => {
        if (thread.id === threadId) {
          return {
            ...thread,
            replies: [...(thread.replies || []), reply]
          };
        }
        return thread;
      });

      const replyViaApi = await patchLocalTask(taskId, { threads: updatedThreads });
      if (!replyViaApi) {
        await updateDoc(doc(db, 'tasks_mops', taskId), {
          threads: updatedThreads,
          updatedAt: serverTimestamp()
        });
      }

      setNewReply('');
      
      // Notify other assignees and creator
      const recipients = new Set([...task.assignedToIds, task.createdBy]);
      recipients.delete(auth.currentUser?.uid || '');

      if (localStorage.getItem('app_auth_token')) {
        await createLocalNotifications(orgId, recipients, {
          title: 'New Reply in Task Discussion',
          message: `${auth.currentUser?.displayName || 'Someone'} replied in a discussion thread.`,
          type: 'task_comment',
          relatedId: taskId,
        });
      } else {
        const notificationPromises = Array.from(recipients).map(userId =>
          addDoc(collection(db, 'notifications'), {
            userId, orgId, title: 'New Reply in Task Discussion',
            message: `${auth.currentUser?.displayName || 'Someone'} replied in a discussion thread.`,
            type: 'task_comment', relatedId: taskId, read: false, createdAt: serverTimestamp()
          })
        );
        await Promise.all(notificationPromises);
      }
      notify.confirm("Reply sent");
    } catch (error) {
      console.error("Error replying to thread:", error);
      notify.error("Failed to send reply");
    }
  };

  const handleRequestExtension = async (taskId: string) => {
    try {
      const task = tasks.find(t => t.id === taskId);
      if (!task) return;

      const request: ExtensionRequest = {
        id: Math.random().toString(36).substr(2, 9),
        days: extensionDays,
        reason: extensionReason,
        status: 'Pending',
        requestedBy: auth.currentUser?.uid || 'unknown',
        requestedByName: auth.currentUser?.displayName || 'Unknown User',
        requestedAt: new Date().toISOString()
      };

      const extListUpdated = [...(task.extensionRequests || []), request];
      const extViaApi = await patchLocalTask(taskId, { extensionRequests: extListUpdated });
      if (!extViaApi) {
        await updateDoc(doc(db, 'tasks_mops', taskId), {
          extensionRequests: extListUpdated,
          updatedAt: serverTimestamp()
        });
      }

      // Notify creator
      if (localStorage.getItem('app_auth_token')) {
        await createLocalNotification(orgId, {
          recipientUserId: task.createdBy,
          title: 'Extension Requested',
          message: `${auth.currentUser?.displayName} requested ${extensionDays} more days for "${task.title}"`,
          type: 'task_extension_request',
          relatedId: taskId,
        });
      } else {
        await addDoc(collection(db, 'notifications'), {
          userId: task.createdBy, orgId, title: 'Extension Requested',
          message: `${auth.currentUser?.displayName} requested ${extensionDays} more days for "${task.title}"`,
          type: 'task_extension_request', relatedId: taskId, read: false, createdAt: serverTimestamp()
        });
      }

      setShowExtensionModal(null);
      setExtensionReason('');
      notify.confirm("Extension request submitted");
    } catch (error) {
      console.error("Error requesting extension:", error);
      notify.error("Failed to submit request");
    }
  };

  const handleApproveExtension = async (taskId: string, requestId: string) => {
    try {
      const task = tasks.find(t => t.id === taskId);
      if (!task) return;

      const request = task.extensionRequests?.find(r => r.id === requestId);
      if (!request) return;

      const currentDueDate = new Date(task.dueDate);
      currentDueDate.setDate(currentDueDate.getDate() + request.days);
      const newDueDate = currentDueDate.toISOString().split('T')[0];

      const approvedExtensions = task.extensionRequests?.map(r => 
        r.id === requestId ? { ...r, status: 'Approved' } : r
      );
      const apprViaApi = await patchLocalTask(taskId, { dueDate: newDueDate, extensionRequests: approvedExtensions });
      if (!apprViaApi) {
        await updateDoc(doc(db, 'tasks_mops', taskId), {
          dueDate: newDueDate,
          extensionRequests: approvedExtensions,
          updatedAt: serverTimestamp()
        });
      }

      // Notify requester
      if (localStorage.getItem('app_auth_token')) {
        await createLocalNotification(orgId, {
          recipientUserId: request.requestedBy,
          title: 'Extension Approved',
          message: `Your extension request for "${task.title}" was approved. New due date: ${newDueDate}`,
          type: 'task_extension_approved',
          relatedId: taskId,
        });
      } else {
        await addDoc(collection(db, 'notifications'), {
          userId: request.requestedBy, orgId, title: 'Extension Approved',
          message: `Your extension request for "${task.title}" was approved. New due date: ${newDueDate}`,
          type: 'task_extension_approved', relatedId: taskId, read: false, createdAt: serverTimestamp()
        });
      }

      notify.confirm("Extension approved and due date updated");
    } catch (error) {
      console.error("Error approving extension:", error);
      notify.error("Failed to approve extension");
    }
  };

  const toggleEmployeeSelection = (emp: Employee, stepIndex?: number) => {
    if (stepIndex !== undefined) {
      const newSteps = [...(newTask.steps || [])];
      const step = newSteps[stepIndex];
      const isSelected = step.assignedToIds.includes(emp.id);
      if (isSelected) {
        step.assignedToIds = step.assignedToIds.filter(id => id !== emp.id);
        step.assignedToNames = step.assignedToNames.filter(name => name !== `${emp.firstName} ${emp.lastName}`);
      } else {
        step.assignedToIds = [...step.assignedToIds, emp.id];
        step.assignedToNames = [...step.assignedToNames, `${emp.firstName} ${emp.lastName}`];
      }
      setNewTask({ ...newTask, steps: newSteps });
    } else {
      const isSelected = newTask.assignedToIds.includes(emp.id);
      if (isSelected) {
        setNewTask({
          ...newTask,
          assignedToIds: newTask.assignedToIds.filter(id => id !== emp.id),
          assignedToNames: newTask.assignedToNames.filter(name => name !== `${emp.firstName} ${emp.lastName}`)
        });
      } else {
        setNewTask({
          ...newTask,
          assignedToIds: [...newTask.assignedToIds, emp.id],
          assignedToNames: [...newTask.assignedToNames, `${emp.firstName} ${emp.lastName}`]
        });
      }
    }
  };

  const handleAddStep = () => {
    setNewTask({
      ...newTask,
      steps: [
        ...(newTask.steps || []),
        { id: Math.random().toString(36).substr(2, 9), title: `Step ${(newTask.steps?.length || 0) + 1}`, assignedToIds: [], assignedToNames: [], status: 'Pending' }
      ]
    });
  };

  const handleRemoveStep = (index: number) => {
    const newSteps = [...(newTask.steps || [])];
    newSteps.splice(index, 1);
    setNewTask({ ...newTask, steps: newSteps });
  };


  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Task/Activities</h1>
          <p className="text-sm opacity-50 italic serif mt-1">Create, assign and track operational tasks and activities</p>
        </div>
        <button 
          onClick={() => {
            setIsEditing(false);
            setNewTask({
              title: '',
              description: '',
              assignedToIds: [],
              assignedToNames: [],
              priority: 'Medium',
              status: 'Pending',
              dueDate: new Date().toISOString().split('T')[0]
            });
            setIsModalOpen(true);
          }}
          className="flex items-center gap-2 bg-[#141414] text-[#E4E3E0] px-6 py-3 rounded-full hover:opacity-90 transition-opacity font-bold"
        >
          <Plus className="w-4 h-4" />
          <span>New Task</span>
        </button>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-white/50 border border-[#141414] p-4 rounded-2xl">
        <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 w-full md:w-auto custom-scrollbar">
          {['All', 'Pending', 'In Progress', 'Completed'].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={cn(
                "px-4 py-2 rounded-full text-xs font-bold transition-all whitespace-nowrap border",
                statusFilter === status 
                  ? "bg-[#141414] text-[#E4E3E0] border-[#141414]" 
                  : "bg-white/50 text-[#141414] border-[#141414]/10 hover:border-[#141414]"
              )}
            >
              {status}
            </button>
          ))}
        </div>
        <div className="relative w-full md:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-50" />
          <input 
            type="text" 
            placeholder="Search tasks or assignees..." 
            className="w-full pl-10 pr-4 py-2 bg-white/50 border border-[#141414]/10 rounded-full outline-none focus:border-[#141414] transition-all text-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20 opacity-50">
          <Loader2 className="w-12 h-12 animate-spin mb-4" />
          <p className="text-xl italic serif">Loading tasks...</p>
        </div>
      ) : filteredTasks.length === 0 ? (
        <div className="text-center py-20 bg-white/30 border-2 border-dashed border-[#141414]/20 rounded-3xl">
          <ListTodo className="w-16 h-16 mx-auto mb-4 opacity-20" />
          <p className="text-xl font-bold opacity-50 italic serif">No tasks found</p>
          <p className="text-sm opacity-30 mt-2">Create your first task to start tracking operations</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTasks.map((task) => (
            <div 
              key={task.id}
              onClick={() => setViewTaskId(task.id)}
              className="bg-white/50 border border-[#141414] rounded-3xl p-6 hover:shadow-xl transition-all group relative overflow-hidden flex flex-col cursor-pointer hover:bg-white"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={cn(
                  "px-3 py-1 rounded-full text-[10px] font-bold border uppercase tracking-widest",
                  getPriorityColor(task.priority)
                )}>
                  {task.priority} Priority
                </div>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={(e) => { e.stopPropagation(); setViewTaskId(task.id); }}
                    className="p-2 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-all opacity-100 relative group-hover:text-[#E4E3E0]"
                    title="Open Thread"
                  >
                    <MessageSquare className="w-4 h-4" />
                            {task.threads?.length > 0 && (
                              <span className="text-xs font-bold text-red-500 ml-1">
                                {task.threads.length}
                              </span>
                            )}
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); setShowExtensionModal(task.id); }}
                    className="p-2 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-all opacity-0 group-hover:opacity-100"
                    title="Request Extension"
                  >
                    <Hourglass className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedTask(task);
                      setNewTask({ 
                        title: task.title,
                        description: task.description,
                        assignedToIds: task.assignedToIds || [],
                        assignedToNames: task.assignedToNames || [],
                        priority: task.priority,
                        status: task.status,
                        dueDate: task.dueDate
                      });
                      setIsEditing(true);
                      setIsModalOpen(true);
                    }}
                    className="p-2 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-all opacity-0 group-hover:opacity-100"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); setDeleteId(task.id); }}
                    className="p-2 hover:bg-red-500 hover:text-white rounded-full transition-all opacity-0 group-hover:opacity-100"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <h3 className="text-lg font-bold mb-2 line-clamp-1">{task.title}</h3>
              <p className="text-sm opacity-60 italic serif line-clamp-2 mb-4 flex-1">
                {task.description || 'No description provided.'}
              </p>

              <div className="space-y-3 pt-4 border-t border-[#141414]/10">
                <div className="flex flex-col gap-1 text-xs">
                  <div className="flex items-center gap-2 opacity-60">
                    <User className="w-3 h-3" />
                    <span>Assigned to:</span>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(task.assignedToNames || []).map((name, idx) => (
                      <span key={idx} className="bg-[#141414]/5 px-2 py-0.5 rounded-full font-bold">
                        {name}
                      </span>
                    ))}
                    {(!task.assignedToNames || task.assignedToNames.length === 0) && (
                      <span className="italic opacity-40">Unassigned</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2 opacity-60">
                    <Calendar className="w-3 h-3" />
                    <span>Due Date:</span>
                  </div>
                  <span className="font-bold">{new Date(task.dueDate).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2 opacity-60">
                    {getStatusIcon(task.status)}
                    <span>Status:</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {task.isWorkflow && task.steps && task.status !== 'Completed' && (
                      <span className="opacity-50 font-bold text-[10px] bg-[#141414]/10 px-2 py-0.5 rounded-full">
                        Step {(task.currentStepIndex || 0) + 1} of {task.steps.length}
                      </span>
                    )}
                    <span className={cn(
                      "font-bold",
                      task.status === 'Completed' ? "text-green-600" : 
                      task.status === 'In Progress' ? "text-blue-600" : "text-gray-600"
                    )}>{task.status}</span>
                  </div>
                </div>
                
                {task.isWorkflow && task.steps && task.status !== 'Completed' && (
                  <div className="mt-4 pt-4 border-t border-[#141414]/10 space-y-2">
                    <p className="text-[10px] font-bold uppercase tracking-widest opacity-40">Current Step: {task.steps[task.currentStepIndex || 0]?.title}</p>
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        // Handle Complete Step
                        const newIndex = (task.currentStepIndex || 0) + 1;
                        const newSteps = [...task.steps!];
                        newSteps[(task.currentStepIndex || 0)].status = 'Completed';
                        const isFinished = newIndex >= newSteps.length;
                        const updates = {
                          steps: newSteps,
                          currentStepIndex: Math.min(newIndex, newSteps.length - 1),
                          status: isFinished ? 'Completed' : 'In Progress',
                          assignedToIds: isFinished ? [] : newSteps[newIndex].assignedToIds,
                          assignedToNames: isFinished ? [] : newSteps[newIndex].assignedToNames
                        };

                        const updatedViaApi = await patchLocalTask(task.id, updates);
                        if (!updatedViaApi) {
                          await updateDoc(doc(db, 'tasks_mops', task.id), {
                            ...updates,
                            updatedAt: serverTimestamp()
                          });

                          if (!isFinished) {
                            newSteps[newIndex].assignedToIds.forEach(userId => {
                              addDoc(collection(db, 'notifications'), {
                                userId,
                                orgId,
                                title: 'Workflow Step Assigned',
                                message: `You have been assigned to step "${newSteps[newIndex].title}" in task "${task.title}".`,
                                type: 'task_assigned',
                                relatedId: task.id,
                                read: false,
                                createdAt: serverTimestamp()
                              });
                            });
                          }
                        } else if (!isFinished) {
                          await createLocalNotifications(orgId, newSteps[newIndex].assignedToIds, {
                            title: 'Workflow Step Assigned',
                            message: `You have been assigned to step "${newSteps[newIndex].title}" in task "${task.title}".`,
                            type: 'task_assigned',
                            relatedId: task.id,
                          });
                        }
                      }}
                      className="w-full bg-[#141414] text-white py-2 rounded-xl text-xs font-bold hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                    >
                      <CheckCircle2 className="w-4 h-4" /> Complete Step
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Task Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 overflow-y-auto overscroll-contain bg-[#141414]/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsModalOpen(false);
          }}
        >
          <div className="bg-[#E4E3E0] border border-[#141414] w-full max-w-2xl rounded-[40px] p-12 shadow-2xl relative overflow-y-auto overscroll-contain max-h-[calc(100dvh-2rem)] custom-scrollbar">
            <button 
              onClick={() => setIsModalOpen(false)}
              className="absolute top-8 right-8 p-2 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="mb-8">
              <h2 className="text-4xl font-bold tracking-tight">{isEditing ? 'Edit Task' : 'New Task'}</h2>
              <p className="text-lg opacity-60 italic serif">Define and assign operational goals</p>
            </div>

            <form onSubmit={handleSaveTask} className="space-y-6">
              <div className="grid grid-cols-1 gap-6">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest opacity-40">Task Title</label>
                  <input 
                    required
                    type="text" 
                    className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all font-bold"
                    placeholder="e.g. Monthly Inventory Audit"
                    value={newTask.title}
                    onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest opacity-40">Description</label>
                  <textarea 
                    className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all italic serif min-h-[100px]"
                    placeholder="Describe the task details..."
                    value={newTask.description}
                    onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                  />
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="isWorkflow"
                    checked={newTask.isWorkflow || false}
                    onChange={(e) => setNewTask({...newTask, isWorkflow: e.target.checked})}
                    className="w-4 h-4 rounded border-[#141414]/20 text-[#141414] focus:ring-[#141414]"
                  />
                  <label htmlFor="isWorkflow" className="text-sm font-bold">This task has a multi-step workflow</label>
                </div>

                {!newTask.isWorkflow ? (
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-widest opacity-40">Assign To (Select Multiple)</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 bg-white/30 border border-[#141414]/10 rounded-2xl custom-scrollbar">
                      {employees.map(emp => {
                        const isSelected = newTask.assignedToIds.includes(emp.id);
                        return (
                          <button
                            key={emp.id}
                            type="button"
                            onClick={() => toggleEmployeeSelection(emp)}
                            className={cn(
                              "flex items-center gap-3 p-3 rounded-xl transition-all border text-left",
                              isSelected 
                                ? "bg-[#141414] text-[#E4E3E0] border-[#141414]" 
                                : "bg-white/50 text-[#141414] border-transparent hover:border-[#141414]/20"
                            )}
                          >
                            <div className={cn(
                              "w-4 h-4 rounded border flex items-center justify-center transition-all",
                              isSelected ? "bg-white border-white" : "border-[#141414]/20"
                            )}>
                              {isSelected && <CheckSquare className="w-3 h-3 text-[#141414]" />}
                            </div>
                            <div className="flex flex-col">
                              <span className="text-sm font-bold">{emp.firstName} {emp.lastName}</span>
                              <span className="text-[10px] opacity-50">{emp.email}</span>
                            </div>
                          </button>
                        );
                      })}
                      {employees.length === 0 && (
                        <div className="col-span-2 py-8 text-center opacity-40 italic text-sm">
                          No employees found in this organization
                        </div>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {newTask.assignedToNames.map((name, idx) => (
                        <span key={idx} className="bg-[#141414] text-[#E4E3E0] px-3 py-1 rounded-full text-[10px] font-bold flex items-center gap-2">
                          {name}
                          <X 
                            className="w-3 h-3 cursor-pointer hover:text-red-400" 
                            onClick={() => {
                              const emp = employees.find(e => `${e.firstName} ${e.lastName}` === name);
                              if (emp) toggleEmployeeSelection(emp);
                            }}
                          />
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-bold uppercase tracking-widest opacity-40">Workflow Steps</label>
                      <button type="button" onClick={handleAddStep} className="text-xs font-bold bg-[#141414] text-white px-3 py-1 rounded-full flex items-center gap-1">
                        <Plus className="w-3 h-3" /> Add Step
                      </button>
                    </div>
                    
                    {(!newTask.steps || newTask.steps.length === 0) && (
                      <div className="text-center py-6 border border-dashed border-[#141414]/20 rounded-2xl opacity-60 text-sm">
                        No steps added. Click 'Add Step' to start building the workflow.
                      </div>
                    )}
                    
                    <div className="space-y-4">
                      {newTask.steps?.map((step, stepIndex) => (
                        <div key={step.id} className="border border-[#141414]/10 bg-white/30 rounded-2xl p-4 space-y-4 relative">
                          <button type="button" onClick={() => handleRemoveStep(stepIndex)} className="absolute top-4 right-4 text-red-500 opacity-60 hover:opacity-100">
                            <Trash2 className="w-4 h-4" />
                          </button>
                          
                          <div className="space-y-4 pr-8">
                            <div className="space-y-2">
                              <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">Step {stepIndex + 1} Name</label>
                              <input
                                type="text"
                                value={step.title}
                                onChange={e => {
                                  const newSteps = [...(newTask.steps || [])];
                                  newSteps[stepIndex].title = e.target.value;
                                  setNewTask({...newTask, steps: newSteps});
                                }}
                                className="w-full bg-white border border-[#141414]/10 p-2.5 rounded-xl outline-none font-bold text-sm"
                                placeholder="e.g. Initial Review"
                              />
                            </div>
                            
                            <div className="space-y-2">
                              <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">Stage Description</label>
                              <textarea
                                value={step.description || ''}
                                onChange={e => {
                                  const newSteps = [...(newTask.steps || [])];
                                  newSteps[stepIndex].description = e.target.value;
                                  setNewTask({...newTask, steps: newSteps});
                                }}
                                className="w-full bg-white border border-[#141414]/10 p-2.5 rounded-xl outline-none text-sm resize-none"
                                rows={2}
                                placeholder="Describe the objectives of this stage..."
                              />
                            </div>

                            <div className="space-y-2">
                              <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">Stage Requirements</label>
                              <textarea
                                value={step.requirements || ''}
                                onChange={e => {
                                  const newSteps = [...(newTask.steps || [])];
                                  newSteps[stepIndex].requirements = e.target.value;
                                  setNewTask({...newTask, steps: newSteps});
                                }}
                                className="w-full bg-white border border-[#141414]/10 p-2.5 rounded-xl outline-none text-sm resize-none"
                                rows={2}
                                placeholder="List exact requirements before moving forward..."
                              />
                            </div>
                          </div>
                          
                          <div className="space-y-2">
                            <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">Assign To</label>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 bg-white/50 border border-[#141414]/10 rounded-xl custom-scrollbar">
                              {employees.map(emp => {
                                const isSelected = step.assignedToIds.includes(emp.id);
                                return (
                                  <button
                                    key={emp.id}
                                    type="button"
                                    onClick={() => toggleEmployeeSelection(emp, stepIndex)}
                                    className={cn(
                                      "flex items-center gap-2 p-2 rounded-lg transition-all border text-left",
                                      isSelected 
                                        ? "bg-[#141414] text-[#E4E3E0] border-[#141414]" 
                                        : "bg-white border-transparent hover:border-[#141414]/20"
                                    )}
                                  >
                                    <div className={cn(
                                      "w-3 h-3 rounded flex flex-shrink-0 items-center justify-center transition-all",
                                      isSelected ? "bg-white text-[#141414]" : "border border-[#141414]/20"
                                    )}>
                                      {isSelected && <CheckSquare className="w-2.5 h-2.5" />}
                                    </div>
                                    <span className="text-xs font-bold truncate">{emp.firstName} {emp.lastName}</span>
                                  </button>
                                );
                              })}
                            </div>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {step.assignedToNames.map((name, idx) => (
                                <span key={idx} className="bg-[#141414] text-[#E4E3E0] px-2 py-0.5 rounded-md text-[10px] font-bold">
                                  {name}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-widest opacity-40">Due Date {isEditing && "(Locked)"}</label>
                    <input 
                      required
                      disabled={isEditing}
                      type="date" 
                      className={cn(
                        "w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all font-bold",
                        isEditing && "opacity-50 cursor-not-allowed"
                      )}
                      value={newTask.dueDate}
                      onChange={(e) => setNewTask({...newTask, dueDate: e.target.value})}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-widest opacity-40">Priority</label>
                    <div className="flex gap-2">
                      {['Low', 'Medium', 'High'].map((p) => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => setNewTask({...newTask, priority: p as any})}
                          className={cn(
                            "flex-1 py-3 rounded-xl text-xs font-bold transition-all border",
                            newTask.priority === p 
                              ? "bg-[#141414] text-[#E4E3E0] border-[#141414]" 
                              : "bg-white/50 text-[#141414] border-[#141414]/10 hover:border-[#141414]"
                          )}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {!newTask.isWorkflow && (
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-widest opacity-40">Status</label>
                    <div className="relative">
                      <select 
                        className="w-full bg-white/50 border border-[#141414] p-4 rounded-2xl outline-none focus:bg-white transition-all appearance-none font-bold"
                        value={newTask.status}
                        onChange={(e) => setNewTask({...newTask, status: e.target.value as any})}
                      >
                        <option value="Pending">Pending</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Completed">Completed</option>
                      </select>
                      <ChevronRight className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 rotate-90 opacity-40" />
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-between pt-8">
                <button 
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-8 py-4 rounded-full font-bold hover:bg-[#141414]/5 transition-all"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  disabled={isSaving}
                  className="bg-[#141414] text-[#E4E3E0] px-12 py-4 rounded-full font-bold hover:opacity-90 transition-opacity flex items-center gap-2"
                >
                  {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                  <span>{isEditing ? 'Update Task' : 'Create Task'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Discussion Thread Modal */}
      {viewTaskId && (
        <div
          className="fixed inset-0 overflow-y-auto overscroll-contain bg-[#141414]/40 backdrop-blur-sm z-[110] flex items-center justify-center p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setViewTaskId(null);
              setActiveThreadId(null);
            }
          }}
        >
          <div className="bg-[#E4E3E0] border border-[#141414] w-full max-w-3xl rounded-[40px] p-8 shadow-2xl relative flex flex-col max-h-[calc(100dvh-2rem)]">
            <button 
              onClick={() => {
                setViewTaskId(null);
                setActiveThreadId(null);
              }}
              className="absolute top-6 right-6 p-2 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="mb-6">
              {(() => {
                const task = tasks.find(t => t.id === viewTaskId);
                if (!task) return null;
                return (
                  <div className="space-y-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <div className={cn(
                            "px-3 py-1 rounded-full text-[10px] font-bold border uppercase tracking-widest",
                            getPriorityColor(task.priority)
                          )}>
                            {task.priority} Priority
                          </div>
                          <span className={cn(
                            "font-bold text-xs uppercase tracking-widest",
                            task.status === 'Completed' ? "text-green-600" : 
                            task.status === 'In Progress' ? "text-blue-600" : "text-gray-600"
                          )}>{task.status}</span>
                        </div>
                        <h2 className="text-3xl font-bold tracking-tight">{task.title}</h2>
                      </div>
                      <div className="flex items-center gap-3">
                        <button 
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            setShowExtensionModal(task.id); 
                          }}
                          className="flex items-center gap-2 bg-[#141414]/5 hover:bg-[#141414] hover:text-[#E4E3E0] px-4 py-2 rounded-full transition-all text-xs font-bold"
                          title="Request Extension"
                        >
                          <Hourglass className="w-3.5 h-3.5" />
                          <span>Request More Time</span>
                        </button>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTask(task);
                            setNewTask({ 
                              title: task.title,
                              description: task.description,
                              assignedToIds: task.assignedToIds || [],
                              assignedToNames: task.assignedToNames || [],
                              priority: task.priority,
                              status: task.status,
                              dueDate: task.dueDate,
                              isWorkflow: task.isWorkflow || false,
                              steps: task.steps || [],
                              currentStepIndex: task.currentStepIndex || 0
                            });
                            setIsEditing(true);
                            setIsModalOpen(true);
                          }}
                          className="p-2 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-all border border-[#141414]/10"
                          title="Edit Task"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            setDeleteId(task.id);
                            setViewTaskId(null);
                          }}
                          className="p-2 hover:bg-red-500 hover:text-white rounded-full transition-all border border-[#141414]/10"
                          title="Delete Task"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    {task.description && (
                      <div className="space-y-2">
                        <p className="text-[10px] font-bold uppercase tracking-widest opacity-40">Task Overview</p>
                        <p className="text-sm opacity-80 leading-relaxed max-w-2xl italic serif">
                          {task.description}
                        </p>
                      </div>
                    )}
                    
                    {task.isWorkflow && task.steps && task.steps.length > 0 && (
                      <div className="bg-white/50 border border-[#141414]/10 rounded-[32px] p-8 mt-6 overflow-hidden">
                        <div className="flex items-center justify-between mb-8">
                           <h3 className="text-sm font-bold uppercase tracking-widest opacity-40 flex items-center gap-2">
                             <ListTodo className="w-4 h-4" />
                             Workflow Progress
                           </h3>
                           <div className="flex gap-4">
                             <div className="flex items-center gap-2 text-[10px] font-bold">
                               <div className="w-2 h-2 rounded-full bg-green-500" />
                               <span className="opacity-60 uppercase">Completed</span>
                             </div>
                             <div className="flex items-center gap-2 text-[10px] font-bold">
                               <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                               <span className="opacity-60 uppercase">In Progress</span>
                             </div>
                           </div>
                        </div>

                        <div className="space-y-8 relative ml-2">
                          <div className="absolute left-[7px] top-2 bottom-2 w-0.5 bg-[#141414]/5" />
                          {task.steps.map((step, idx) => {
                            const isCurrent = idx === (task.currentStepIndex || 0) && task.status !== 'Completed';
                            const isCompleted = idx < (task.currentStepIndex || 0) || task.status === 'Completed';
                            const isPending = idx > (task.currentStepIndex || 0) && task.status !== 'Completed';

                            return (
                              <div key={step.id} className="relative pl-10">
                                <div className={cn(
                                  "absolute left-0 top-1.5 w-4 h-4 rounded-full border-2 bg-white z-10 flex items-center justify-center transition-all",
                                  isCurrent ? "border-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.3)]" : 
                                  isCompleted ? "border-green-500 bg-green-500" : "border-[#141414]/10"
                                )}>
                                  {isCompleted && <Check className="w-2.5 h-2.5 text-white" />}
                                  {isCurrent && <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />}
                                </div>
                                
                                <div className={cn(
                                  "space-y-4 rounded-2xl p-5 transition-all border",
                                  isCurrent ? "bg-blue-50/30 border-blue-100 ring-4 ring-blue-50/10" : 
                                  isCompleted ? "bg-green-50/20 border-green-50 opacity-60" : "bg-white/30 border-transparent opacity-40"
                                )}>
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <span className="font-bold text-base uppercase tracking-tight">{step.title}</span>
                                      {isCurrent && (
                                        <span className="text-[9px] bg-blue-500 text-white px-2 py-0.5 rounded-full font-bold uppercase tracking-widest shadow-sm">Working On This Now</span>
                                      )}
                                      {isPending && (
                                        <span className="text-[9px] bg-gray-200 text-gray-400 px-2 py-0.5 rounded-full font-bold uppercase tracking-widest">Pending</span>
                                      )}
                                    </div>
                                    <div className="flex -space-x-1.5">
                                      {step.assignedToNames.map((name, midx) => (
                                        <div 
                                          key={midx} 
                                          className="w-7 h-7 rounded-full bg-[#141414] text-[#E4E3E0] border-2 border-white flex items-center justify-center text-[10px] font-bold shadow-sm"
                                          title={name}
                                        >
                                          {name.charAt(0)}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                  
                                  {step.description && (
                                    <div className="space-y-1.5">
                                      <p className="text-[9px] font-bold opacity-30 uppercase tracking-widest">Description</p>
                                      <p className="text-xs opacity-70 italic serif leading-relaxed">{step.description}</p>
                                    </div>
                                  )}
                                  
                                  {step.requirements && (
                                    <div className="space-y-1.5">
                                      <p className="text-[9px] font-bold opacity-30 uppercase tracking-widest text-red-700/60">Stage Requirements</p>
                                      <p className="text-xs opacity-60 italic serif leading-relaxed text-red-900/80">{step.requirements}</p>
                                    </div>
                                  )}
                                  
                                  <div className="flex flex-wrap gap-x-4 gap-y-1 pt-2 border-t border-[#141414]/5">
                                    {step.assignedToNames.map((name, nidx) => (
                                      <span key={nidx} className="flex items-center gap-1.5 text-[10px] font-bold opacity-50">
                                        <User className="w-3 h-3" />
                                        {name}
                                      </span>
                                    ))}
                                  </div>

                                  {isCurrent && (
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        const newIndex = (task.currentStepIndex || 0) + 1;
                                        const newSteps = [...task.steps!];
                                        newSteps[(task.currentStepIndex || 0)].status = 'Completed';
                                        const isFinished = newIndex >= newSteps.length;
                                        const updates = {
                                          steps: newSteps,
                                          currentStepIndex: Math.min(newIndex, newSteps.length - 1),
                                          status: isFinished ? 'Completed' : 'In Progress',
                                          assignedToIds: isFinished ? [] : newSteps[newIndex].assignedToIds,
                                          assignedToNames: isFinished ? [] : newSteps[newIndex].assignedToNames
                                        };

                                        const updatedViaApi = await patchLocalTask(task.id, updates);
                                        if (!updatedViaApi) {
                                          await updateDoc(doc(db, 'tasks_mops', task.id), {
                                            ...updates,
                                            updatedAt: serverTimestamp()
                                          });
                                        }

                                        notify.confirm(isFinished ? "Workflow completed!" : "Step finalized");
                                      }}
                                      className="mt-2 w-full bg-[#141414] text-white py-2.5 rounded-xl text-xs font-bold hover:opacity-90 transition-all flex items-center justify-center gap-2 shadow-lg hover:translate-y-[-1px] active:translate-y-[0px]"
                                    >
                                      <CheckCircle2 className="w-4 h-4" /> Finalize Stage & Move to Next
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            <div className="flex items-center justify-between mb-4 border-t border-[#141414]/10 pt-6">
              <h2 className="text-xl font-bold tracking-tight">Discussions</h2>
              {activeThreadId && (
                <button 
                  onClick={() => setActiveThreadId(null)}
                  className="text-xs font-bold uppercase tracking-widest bg-[#141414] text-[#E4E3E0] px-4 py-2 rounded-full hover:opacity-90 transition-opacity"
                >
                  Back to Threads
                </button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
              {!activeThreadId ? (
                <div className="space-y-6">
                  {/* Create New Thread */}
                  <div className="bg-white border border-[#141414] p-6 rounded-[32px] shadow-sm">
                    <h3 className="text-sm font-bold uppercase tracking-widest mb-4 opacity-40">Start a New Thread</h3>
                    <div className="flex gap-3">
                      <input 
                        type="text"
                        placeholder="Thread title or topic..."
                        className="flex-1 bg-[#E4E3E0]/30 border border-[#141414]/10 p-4 rounded-2xl outline-none focus:border-[#141414] transition-all"
                        value={newThreadTitle}
                        onChange={(e) => setNewThreadTitle(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleCreateThread(viewTaskId)}
                      />
                      <button 
                        onClick={() => handleCreateThread(viewTaskId)}
                        className="bg-[#141414] text-[#E4E3E0] px-6 py-4 rounded-2xl font-bold hover:opacity-90 transition-opacity flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Start</span>
                      </button>
                    </div>
                  </div>

                  {/* Threads List */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-bold uppercase tracking-widest opacity-40">Existing Discussions</h3>
                    {tasks.find(t => t.id === viewTaskId)?.threads?.map((thread) => (
                      <button
                        key={thread.id}
                        onClick={() => setActiveThreadId(thread.id)}
                        className="w-full text-left bg-white border border-[#141414] p-6 rounded-[32px] hover:shadow-md transition-all group"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-bold uppercase tracking-widest opacity-40">{thread.userName}</span>
                          <span className="text-[10px] opacity-30">{new Date(thread.createdAt).toLocaleString()}</span>
                        </div>
                        <h4 className="text-lg font-bold mb-2 group-hover:text-primary transition-colors">{thread.title}</h4>
                        <div className="flex items-center gap-2 text-xs opacity-60">
                          <MessageSquare className="w-4 h-4" />
                          <span>{thread.replies?.length || 0} replies</span>
                        </div>
                      </button>
                    ))}
                    {(!tasks.find(t => t.id === viewTaskId)?.threads || tasks.find(t => t.id === viewTaskId)?.threads?.length === 0) && (
                      <div className="text-center py-10 opacity-40 italic serif">
                        No discussion threads yet.
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col h-full">
                  {/* Thread Header */}
                  <div className="bg-white border border-[#141414] p-6 rounded-[32px] mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold uppercase tracking-widest opacity-40">
                        Thread started by {tasks.find(t => t.id === viewTaskId)?.threads?.find(th => th.id === activeThreadId)?.userName}
                      </span>
                      <span className="text-[10px] opacity-30">
                        {new Date(tasks.find(t => t.id === viewTaskId)?.threads?.find(th => th.id === activeThreadId)?.createdAt).toLocaleString()}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold">{tasks.find(t => t.id === viewTaskId)?.threads?.find(th => th.id === activeThreadId)?.title}</h3>
                  </div>

                  {/* Replies List */}
                  <div className="flex-1 space-y-4 mb-6">
                    {tasks.find(t => t.id === viewTaskId)?.threads?.find(th => th.id === activeThreadId)?.replies?.map((reply) => (
                      <div key={reply.id} className={cn(
                        "p-4 rounded-2xl max-w-[85%]",
                        reply.userId === auth.currentUser?.uid 
                          ? "bg-[#141414] text-[#E4E3E0] ml-auto shadow-lg" 
                          : "bg-white border border-[#141414]/10 shadow-sm"
                      )}>
                        <div className="flex items-center justify-between mb-1 gap-4">
                          <span className="text-[10px] font-bold uppercase tracking-widest opacity-60">{reply.userName}</span>
                          <span className="text-[8px] opacity-40">{new Date(reply.createdAt).toLocaleString()}</span>
                        </div>
                        <p className="text-sm leading-relaxed">{reply.text}</p>
                      </div>
                    ))}
                    {(!tasks.find(t => t.id === viewTaskId)?.threads?.find(th => th.id === activeThreadId)?.replies || 
                       tasks.find(t => t.id === viewTaskId)?.threads?.find(th => th.id === activeThreadId)?.replies?.length === 0) && (
                      <div className="text-center py-10 opacity-40 italic serif">
                        No replies yet. Be the first to reply!
                      </div>
                    )}
                  </div>

                  {/* Reply Input */}
                  <div className="sticky bottom-0 bg-[#E4E3E0] pt-2">
                    <div className="relative">
                      <input 
                        type="text"
                        placeholder="Type your reply..."
                        className="w-full bg-white border border-[#141414] p-4 pr-14 rounded-2xl outline-none focus:ring-2 ring-primary/20 transition-all font-bold"
                        value={newReply}
                        onChange={(e) => setNewReply(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleReplyToThread(viewTaskId, activeThreadId)}
                      />
                      <button 
                        onClick={() => handleReplyToThread(viewTaskId, activeThreadId)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-2 bg-[#141414] text-[#E4E3E0] rounded-xl hover:opacity-90 transition-opacity"
                      >
                        <Send className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Extension Request Modal */}
      {showExtensionModal && (
        <div
          className="fixed inset-0 overflow-y-auto overscroll-contain bg-[#141414]/40 backdrop-blur-sm z-[110] flex items-center justify-center p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setShowExtensionModal(null);
          }}
        >
          <div className="bg-[#E4E3E0] border border-[#141414] w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain rounded-[40px] p-8 shadow-2xl relative">
            <button 
              onClick={() => setShowExtensionModal(null)}
              className="absolute top-6 right-6 p-2 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="mb-6">
              <h2 className="text-2xl font-bold tracking-tight">Request More Time</h2>
              <p className="text-sm opacity-60 italic serif">Current Due Date: {new Date(tasks.find(t => t.id === showExtensionModal)?.dueDate || '').toLocaleDateString()}</p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">How many extra days?</label>
                <div className="flex gap-2">
                  {[2, 3, 7].map((days) => (
                    <button
                      key={days}
                      onClick={() => setExtensionDays(days)}
                      className={cn(
                        "flex-1 py-3 rounded-xl text-xs font-bold transition-all border",
                        extensionDays === days 
                          ? "bg-[#141414] text-[#E4E3E0] border-[#141414]" 
                          : "bg-white text-[#141414] border-[#141414]/10 hover:border-[#141414]"
                      )}
                    >
                      {days === 7 ? '1 Week' : `${days} Days`}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest opacity-40">Reason for extension</label>
                <textarea 
                  className="w-full bg-white border border-[#141414] p-4 rounded-2xl outline-none focus:ring-2 ring-primary/20 transition-all italic serif min-h-[100px]"
                  placeholder="Why do you need more time?"
                  value={extensionReason}
                  onChange={(e) => setExtensionReason(e.target.value)}
                />
              </div>

              <button 
                onClick={() => handleRequestExtension(showExtensionModal)}
                className="w-full bg-[#141414] text-[#E4E3E0] py-4 rounded-2xl font-bold hover:opacity-90 transition-opacity mt-4"
              >
                Submit Request
              </button>
            </div>

            {/* My Requests (for requester to see/remove) */}
            <div className="mt-8 pt-8 border-t border-[#141414]/10 space-y-4">
              <h3 className="text-xs font-bold uppercase tracking-widest opacity-40 text-[#141414]">My Requests</h3>
              <div className="space-y-4">
                {tasks.find(t => t.id === showExtensionModal)?.extensionRequests?.filter(r => r.requestedBy === auth.currentUser?.uid).map((req) => (
                  <div key={req.id} className="bg-white p-4 rounded-2xl border border-[#141414]/10 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <span className={cn(
                        "text-[10px] font-bold uppercase tracking-widest",
                        req.status === 'Approved' ? "text-green-600" : req.status === 'Rejected' ? "text-red-500" : "text-primary"
                      )}>
                        {req.status}
                      </span>
                      <span className="text-[10px] bg-primary/10 text-primary px-2 py-0.5 rounded-full font-bold">+{req.days} days</span>
                    </div>
                    <p className="text-xs opacity-60 italic serif mb-4">{req.reason}</p>
                    {req.status === 'Pending' && (
                      <button 
                        onClick={async () => {
                          const task = tasks.find(t => t.id === showExtensionModal);
                          if (!task) return;
                          const filtered = task.extensionRequests?.filter(r => r.id !== req.id);
                          const viaApi = await patchLocalTask(showExtensionModal, { extensionRequests: filtered });
                          if (!viaApi) {
                            await updateDoc(doc(db, 'tasks_mops', showExtensionModal), {
                              extensionRequests: filtered
                            });
                          }
                          notify.confirm("Extension request removed");
                        }}
                        className="w-full flex items-center justify-center gap-2 bg-red-500 text-white py-3 rounded-xl text-[10px] font-bold hover:opacity-90 transition-all shadow-sm"
                      >
                        <Trash2 className="w-3 h-3" /> Remove My Pending Request
                      </button>
                    )}
                  </div>
                ))}
                {(!tasks.find(t => t.id === showExtensionModal)?.extensionRequests || 
                  tasks.find(t => t.id === showExtensionModal)?.extensionRequests?.filter(r => r.requestedBy === auth.currentUser?.uid).length === 0) && (
                  <p className="text-center text-[10px] opacity-40 italic">You haven't requested any extensions yet.</p>
                )}
              </div>
            </div>

            {/* Pending Requests (for creator to approve) */}
            {tasks.find(t => t.id === showExtensionModal)?.createdBy === auth.currentUser?.uid && (
              <div className="mt-8 pt-8 border-t border-[#141414]/10 space-y-4">
                <h3 className="text-xs font-bold uppercase tracking-widest opacity-40 text-[#141414]">Approvals Needed</h3>
                {tasks.find(t => t.id === showExtensionModal)?.extensionRequests?.filter(r => r.status === 'Pending').map((req) => (
                  <div key={req.id} className="bg-white p-4 rounded-2xl border border-[#141414]/10">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold">{req.requestedByName}</span>
                      <span className="text-[10px] bg-primary/10 text-primary px-2 py-0.5 rounded-full">+{req.days} days</span>
                    </div>
                    <p className="text-xs opacity-60 italic serif mb-4">{req.reason}</p>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => handleApproveExtension(showExtensionModal, req.id)}
                        className="flex-1 flex items-center justify-center gap-1 bg-green-500 text-white py-2 rounded-xl text-[10px] font-bold hover:opacity-90"
                      >
                        <Check className="w-3 h-3" /> Approve
                      </button>
                      <button 
                        onClick={async () => {
                          const task = tasks.find(t => t.id === showExtensionModal);
                          if (!task) return;
                          const rejected = task.extensionRequests?.map(r => 
                            r.id === req.id ? { ...r, status: 'Rejected' } : r
                          );
                          const viaApi = await patchLocalTask(showExtensionModal, { extensionRequests: rejected });
                          if (!viaApi) {
                            await updateDoc(doc(db, 'tasks_mops', showExtensionModal), {
                              extensionRequests: rejected
                            });
                          }
                          notify.error("Extension request rejected");
                        }}
                        className="flex-1 flex items-center justify-center gap-1 bg-red-500 text-white py-2 rounded-xl text-[10px] font-bold hover:opacity-90"
                      >
                        <Ban className="w-3 h-3" /> Reject
                      </button>
                    </div>
                  </div>
                ))}
                {tasks.find(t => t.id === showExtensionModal)?.extensionRequests?.filter(r => r.status === 'Pending').length === 0 && (
                  <p className="text-center text-[10px] opacity-40 italic">No pending requests</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmModal 
        isOpen={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId && handleDeleteTask(deleteId)}
        title="Delete Task"
        message="Are you sure you want to delete this task? This action cannot be undone."
      />
    </div>
  );
}
